import { readDevDateOverride } from "../devDate";

const API_BASE = "/api/v1";
export const SUPPORT_VIEW_TOKEN_KEY = "financeSupportViewToken";
export const SUPPORT_VIEW_GROUP_KEY = "financeSupportViewGroupId";
export const SUPPORT_VIEW_READONLY_MESSAGE = "Admin cannot modify records in Support view";

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export function isGroupAccessBlocked(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  const detail = error.body && typeof error.body === "object" ? (error.body as { detail?: unknown }).detail : undefined;
  if (detail === "Group access blocked") return true;
  if (detail && typeof detail === "object") {
    return Boolean((detail as { blocked_group?: unknown }).blocked_group) || (detail as { code?: unknown }).code === "group_blocked";
  }
  return false;
}

export function isSupportView() {
  return Boolean(localStorage.getItem(SUPPORT_VIEW_TOKEN_KEY));
}

export function startSupportView(accessToken: string, groupId: string) {
  localStorage.setItem(SUPPORT_VIEW_TOKEN_KEY, accessToken);
  localStorage.setItem(SUPPORT_VIEW_GROUP_KEY, groupId);
}

export function stopSupportView() {
  localStorage.removeItem(SUPPORT_VIEW_TOKEN_KEY);
  localStorage.removeItem(SUPPORT_VIEW_GROUP_KEY);
}

export function isSupportViewReadonlyError(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  const detail = error.body && typeof error.body === "object" ? (error.body as { detail?: unknown }).detail : undefined;
  if (typeof detail === "string") return detail === SUPPORT_VIEW_READONLY_MESSAGE;
  return Boolean(
    detail &&
      typeof detail === "object" &&
      ((detail as { code?: unknown }).code === "support_view_readonly" ||
        (detail as { message?: unknown }).message === SUPPORT_VIEW_READONLY_MESSAGE),
  );
}

function getAuthToken() {
  return localStorage.getItem(SUPPORT_VIEW_TOKEN_KEY) || localStorage.getItem("financeToken");
}

function notifySupportViewReadonly(error: ApiError) {
  if (!isSupportViewReadonlyError(error)) return;
  window.dispatchEvent(new CustomEvent("finance:support-view-readonly"));
}

function formatApiError(body: unknown, fallback: string) {
  if (typeof body === "string") return body || fallback || "Request failed";
  if (!body || typeof body !== "object") return fallback || "Request failed";
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item || typeof item !== "object") return "Invalid value";
        const loc = Array.isArray((item as { loc?: unknown }).loc) ? (item as { loc: unknown[] }).loc : [];
        const field = loc.filter((part) => part !== "body").join(".");
        const message = String((item as { msg?: unknown }).msg || "Invalid value");
        return field ? `${field}: ${message}` : message;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    return String((detail as { message?: unknown; msg?: unknown }).message || (detail as { msg?: unknown }).msg || JSON.stringify(detail));
  }
  return fallback || "Request failed";
}

function parseApiBody(text: string) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function withDevDateOverride(path: string, method?: string) {
  const normalizedMethod = (method || "GET").toUpperCase();
  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD") return path;
  const asOf = readDevDateOverride();
  if (!asOf) return path;
  const [pathname, query = ""] = path.split("?", 2);
  const params = new URLSearchParams(query);
  if (!params.has("as_of")) params.set("as_of", asOf);
  return `${pathname}?${params.toString()}`;
}

export async function api<T>(path: string, options: RequestInit & { groupId?: string } = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...Object.fromEntries(new Headers(options.headers).entries()),
  };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.groupId) headers["X-Finance-Group-Id"] = options.groupId;

  const response = await fetch(`${API_BASE}${withDevDateOverride(path, options.method)}`, {
    ...options,
    credentials: "same-origin",
    headers,
  });
  if (response.status === 204) return null as T;

  const text = await response.text();
  const body = parseApiBody(text);
  if (!response.ok) {
    const error = new ApiError(formatApiError(body, response.statusText), response.status, body);
    notifySupportViewReadonly(error);
    throw error;
  }
  return body as T;
}

export async function apiBlob(path: string, options: RequestInit & { groupId?: string } = {}): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {
    ...Object.fromEntries(new Headers(options.headers).entries()),
  };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.groupId) headers["X-Finance-Group-Id"] = options.groupId;

  const response = await fetch(`${API_BASE}${withDevDateOverride(path, options.method)}`, {
    ...options,
    credentials: "same-origin",
    headers,
  });
  if (!response.ok) {
    const text = await response.text();
    const body = parseApiBody(text);
    const error = new ApiError(formatApiError(body, response.statusText), response.status, body);
    notifySupportViewReadonly(error);
    throw error;
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] || "aibros_finance_export";
  return { blob: await response.blob(), filename };
}

export async function apiFormData<T>(path: string, formData: FormData, options: RequestInit & { groupId?: string } = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...Object.fromEntries(new Headers(options.headers).entries()),
  };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.groupId) headers["X-Finance-Group-Id"] = options.groupId;

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    method: options.method || "POST",
    body: formData,
    credentials: "same-origin",
    headers,
  });
  if (response.status === 204) return null as T;

  const text = await response.text();
  const body = parseApiBody(text);
  if (!response.ok) {
    const error = new ApiError(formatApiError(body, response.statusText), response.status, body);
    notifySupportViewReadonly(error);
    throw error;
  }
  return body as T;
}
